/**
 * Der einzige Weg, wie das Frontend mit der API spricht.
 *
 * Kein Komponenten-Code ruft `fetch` direkt auf. Hier stehen die drei Dinge, die
 * sonst an fuenfzehn Stellen wiederholt (und an einer vergessen) würden:
 *  - Cookies mitschicken,
 *  - den CSRF-Token in verändernde Anfragen setzen,
 *  - Fehlerantworten in eine `ApiRequestError` mit Feld-Details verwandeln.
 */

import type { ApiError } from '@pmpro/shared';

/** Vom Server geliefertes Fehlerobjekt, in eine Exception verpackt. */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** Feldbezogene Meldungen: Feldname -> Text, direkt unter dem Feld anzeigbar. */
    readonly details: Record<string, string> = {}
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

/**
 * Der CSRF-Token der laufenden Sitzung. Er kommt aus der Login-Antwort bzw. aus
 * `GET /api/auth/me` und liegt hier als Modulvariable, damit der Client ihn setzen
 * kann, ohne den Auth-Store zu importieren (das wäre ein Import-Zirkel).
 */
let csrfToken = '';

export function setCsrfToken(token: string): void {
  csrfToken = token;
}

const SAFE_METHODS = new Set(['GET', 'HEAD']);

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * Setzt den Basispfad des SPA vor einen API-Pfad: `/api/x` bleibt bei Basis `/`
 * unverändert und wird bei Basis `/docker/pmpro/` zu `/docker/pmpro/api/x`.
 * Aufrufer schreiben weiter `/api/…` — der Unterpfad ist allein Sache des Builds.
 */
export function apiUrl(path: string, basis: string = import.meta.env.BASE_URL): string {
  return basis.replace(/\/+$/, '') + path;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {};

  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (!SAFE_METHODS.has(method) && csrfToken) headers['x-csrf-token'] = csrfToken;

  const response = await fetch(apiUrl(path), {
    method,
    headers,
    // Gleiche Herkunft in Entwicklung (Vite-Proxy) wie in Produktion — deshalb
    // genügt `same-origin`.
    credentials: 'same-origin',
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    ...(options.signal ? { signal: options.signal } : {})
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const error = (payload as ApiError | null)?.error;
    throw new ApiRequestError(
      response.status,
      error?.code ?? 'UNKNOWN',
      error?.message ?? 'Die Anfrage ist fehlgeschlagen.',
      error?.details ?? {}
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' })
};

/**
 * Hebt die Feld-Details eines Validierungsfehlers heraus und liefert sonst eine
 * Meldung für den Formularkopf. Spart in jedem Formular denselben try/catch-Block.
 */
export function toFormErrors(error: unknown): {
  message: string;
  fields: Record<string, string>;
} {
  if (error instanceof ApiRequestError) {
    return { message: error.message, fields: error.details };
  }
  return { message: 'Die Verbindung zum Server ist fehlgeschlagen.', fields: {} };
}
